import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Loader2, Trash2, Check, ChevronDown } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

// Backend types survive untouched; what changed is the shelving. A seller
// thinks "my products" and "my scenes", not our storage taxonomy, so product
// and outfit share one grid (an outfit is a garment worn instead of held —
// a badge and a toggle, not a separate room) and model hides behind an
// "Extra faces" tab that only exists once such assets exist: People is where
// faces belong now.
const TABS = [
  { key: 'products', label: 'Products', types: ['product', 'outfit'] },
  { key: 'scenes', label: 'Scenes', types: ['environment'] },
  { key: 'faces', label: 'Extra faces', types: ['model'] },
];

const TAB_BLURB = {
  products: 'Wired into a person, a product is held up and shown to the camera; a garment is worn instead.',
  scenes: 'Where the photograph is taken.',
  faces: 'A described or uploaded person for shots where nobody real is needed.',
};

// The per-card type select keeps every backend shelf reachable, because the
// same cap really can be product one day and outfit the next.
const TYPES = [
  { key: 'product', label: 'Product' },
  { key: 'outfit', label: 'Outfit' },
  { key: 'model', label: 'Model' },
  { key: 'environment', label: 'Environment' },
];

export default function Assets() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [tab, setTab] = useState('products');
  const [uploading, setUploading] = useState(false);
  const [rights, setRights] = useState(false);
  // On the Products tab the upload makes a product unless this says garment.
  const [asGarment, setAsGarment] = useState(false);
  // Which asset cards have the Advanced (prompt fragment) section open.
  const [advOpen, setAdvOpen] = useState({});
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    try { setAssets(await api.listAssets()); } catch (e) {
      toast({ title: 'Could not load your assets', description: 'Check your connection and reload.' });
    }
  };
  useEffect(() => { load(); }, []);

  // Poll while any asset is still analysing
  useEffect(() => {
    const analysing = assets.some((a) => a.status !== 'ready');
    if (analysing) {
      pollRef.current = setTimeout(load, 2500);
      return () => clearTimeout(pollRef.current);
    }
  }, [assets]);

  // What type the upload button on the current tab creates.
  const uploadType = tab === 'scenes' ? 'environment'
    : tab === 'faces' ? 'model'
    : (asGarment ? 'outfit' : 'product');

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (uploadType === 'model' && !rights) {
      toast({ title: 'Confirm image rights', description: 'Please confirm you own rights to model images before uploading.' });
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.createAsset({ type: uploadType, name: file.name.replace(/\.[^.]+$/, ''), dataUrl: reader.result, tags: '' });
        toast({ title: 'Asset uploaded', description: 'Analysing to build its prompt fragment…' });
        await load();
      } catch (err) {
        toast({ title: 'Upload failed', description: err.message });
      }
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const remove = async (id) => {
    // The backend answers 409 {used_in: N} when a session still references
    // this asset: it keeps rendering (the fragment is baked into the graph)
    // but loses its reference photo, so the deletion deserves a heads-up and
    // an explicit force retry.
    try {
      await api.deleteAsset(id);
    } catch (e) {
      const used = e?.detail?.used_in;
      if (e.status === 409) {
        const n = used || 'some';
        const ok = window.confirm(
          `This asset is used by ${n} session${used === 1 ? '' : 's'}.\n\n`
          + 'They will keep rendering, but lose this reference photo. Delete anyway?');
        if (!ok) return;
        try { await api.deleteAsset(id, true); }
        catch (e2) { toast({ title: 'Could not delete it', description: e2.message }); return; }
      } else {
        toast({ title: 'Could not delete it', description: e.message });
        return;
      }
    }
    setAssets((prev) => prev.filter((a) => a.id !== id));
  };

  const saveFragment = async (id, prompt_fragment) => {
    await api.patchAsset(id, { prompt_fragment });
    toast({ title: 'Saved', description: 'Prompt fragment updated.' });
  };

  // The name was display-only, so an asset uploaded as 9798235595255.png kept
  // that as its name for good. It is what the script writer calls the product
  // out loud, so it has to be editable.
  const saveName = async (id, name) => {
    const clean = (name || '').trim();
    if (!clean) return;
    const current = assets.find((a) => a.id === id);
    if (current && current.name === clean) return;
    try {
      await api.patchAsset(id, { name: clean });
      setAssets((prev) => prev.map((a) => (a.id === id ? { ...a, name: clean } : a)));
      toast({ title: 'Renamed', description: clean });
    } catch (e) {
      toast({ title: 'Could not rename', description: e.message });
    }
  };

  // Moving an asset between shelves. It leaves the list being looked at, which
  // is the right feedback: it is somewhere else now.
  const changeType = async (id, next) => {
    const before = assets;
    setAssets((prev) => prev.map((x) => (x.id === id ? { ...x, type: next } : x)));
    try {
      await api.patchAsset(id, { type: next });
      toast({ title: `Now a ${next}`,
              description: next === 'outfit'
                ? 'Wired into a person, it will be worn.'
                : next === 'product'
                  ? 'Wired into a person, it will be held up to the camera.'
                  : `Moved to ${next}.` });
    } catch (e) {
      setAssets(before);
      toast({ title: 'Could not change the type', description: e.message });
    }
  };

  // Extra faces only earns a tab while model assets exist; nobody should meet
  // an empty legacy shelf whose real home is People.
  const hasModels = assets.some((a) => a.type === 'model');
  const tabs = TABS.filter((t) => t.key !== 'faces' || hasModels);
  const active = TABS.find((t) => t.key === tab) || TABS[0];
  const shown = assets.filter((a) => active.types.includes(a.type));

  return (
    <div className="min-h-screen bg-[#0E0E10] text-white">
      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h1 className="font-serif-display text-3xl">Products &amp; scenes</h1>
          <span className="shrink-0 text-sm text-white/50">{user?.storage_used_mb ?? 0} MB used</span>
        </div>
        {tab === 'faces' && (
          <p className="mt-2 text-xs text-white/50">
            Looking for your saved people? <Link to="/app/influencers" className="text-node-model hover:underline">→ People</Link>
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2">
            {tabs.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`assets-tab-${t.key}`} className={`rounded-full border px-4 py-2 text-sm transition-colors ${tab === t.key ? 'border-white bg-white text-black' : 'border-[#2A2A30] text-white/60 hover:text-white'}`}>{t.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {tab === 'products' && (
              <label className="flex items-center gap-2 text-xs text-white/60" title="A garment is worn by the person instead of held up to the camera.">
                <input type="checkbox" checked={asGarment} onChange={(e) => setAsGarment(e.target.checked)} data-testid="upload-garment-toggle" className="accent-node-product" />
                This is a garment
              </label>
            )}
            {tab === 'faces' && (
              <label className="flex items-center gap-2 text-xs text-white/60">
                <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)} className="accent-node-style" />
                I own rights to these images
              </label>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" />
            <p className="w-full text-[11px] leading-relaxed text-white/35">{TAB_BLURB[tab]}</p>
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex h-10 items-center gap-1.5 rounded-full bg-white px-5 text-sm font-medium text-black hover:bg-white/90 disabled:opacity-50">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload {uploadType === 'environment' ? 'scene' : uploadType}
            </button>
          </div>
        </div>

        {shown.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-[#2A2A30] p-12 text-center text-white/50">
            No {active.label.toLowerCase()} yet. Upload one to build your brand memory.
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((a) => (
              <div key={a.id} className="overflow-hidden rounded-2xl border border-[#2A2A30] bg-[#141417]">
                <div className="relative aspect-video w-full overflow-hidden bg-[#0E0E10]">
                  {a.url && <img src={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} alt={a.name} className="h-full w-full object-cover" />}
                  {/* Outfits share the Products grid, so the difference has to
                      be visible without opening the type select. */}
                  {a.type === 'outfit' && (
                    <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] text-node-product">garment</span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2">
                    <input
                      defaultValue={a.name || ''}
                      onBlur={(e) => saveName(a.id, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                      data-testid={`asset-name-${a.id}`}
                      placeholder="Name this asset"
                      className="min-w-0 flex-1 truncate rounded-md border border-transparent bg-transparent px-1.5 py-1 font-medium text-white hover:border-[#2A2A30] focus:border-white/30 focus:bg-[#0E0E10] focus:outline-none"
                    />
                    <button onClick={() => remove(a.id)} className="shrink-0 text-white/40 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <p className="flex items-center gap-1 text-[11px] text-white/40">
                      {a.status === 'ready' ? <><Check size={11} className="text-node-environment" /> analysed</> : <><Loader2 size={11} className="animate-spin" /> analysing…</>}
                    </p>
                    {/* An Outfit is worn and a Product is held up to the lens, so
                        this is not filing: it is the instruction. The same cap can
                        be either, depending on the photograph wanted that day. */}
                    <select
                      value={a.type}
                      onChange={(e) => changeType(a.id, e.target.value)}
                      data-testid={`asset-type-${a.id}`}
                      title="Outfit is worn. Product is held up and shown to camera."
                      className="rounded-md border border-[#2A2A30] bg-[#0E0E10] px-1.5 py-0.5 text-[11px] text-white/60 focus:border-white/30 focus:outline-none"
                    >
                      {TYPES.map((t) => (
                        <option key={t.key} value={t.key}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setAdvOpen((p) => ({ ...p, [a.id]: !p[a.id] }))}
                      data-testid={`asset-advanced-${a.id}`}
                      className="flex items-center gap-1 text-[11px] text-white/40 hover:text-white/70"
                    >
                      <ChevronDown size={12} className={`transition-transform ${advOpen[a.id] ? 'rotate-180' : ''}`} /> Advanced
                    </button>
                    {advOpen[a.id] && (
                      <textarea
                        defaultValue={a.prompt_fragment || ''}
                        onBlur={(e) => saveFragment(a.id, e.target.value)}
                        rows={3}
                        className="mt-2 w-full resize-none rounded-md border border-[#2A2A30] bg-[#0E0E10] p-2 text-xs text-white/80 focus:border-white/30 focus:outline-none"
                        placeholder="Prompt fragment…"
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
